import type { CdtRuleSet } from "@CommonApi/merchflows/_merchflowId_/cdt/compression";
import { omniAtom } from "src/utils/atoms";

export type ReleaseWallBlocking = NonNullable<CdtRuleSet["wallBlocking"]>;

/**
 * What the CDT Studio's state means for the primary blocking mode of a store
 * generation started from this base planogram.
 *
 * The mode follows the studio rather than being chosen independently of it: a
 * release blocks the wall the way the studio read it (columns mean vertical
 * blocking, rows mean horizontal), and skipped means horizontal on the category
 * CDTs. Where the studio cannot open at all there is nothing to follow, and the
 * rules keep the defaults they always had.
 */
export type StudioBlockingState =
  | { kind: "studioUnavailable" }
  | { kind: "finalized"; releaseId: number; wallBlocking: ReleaseWallBlocking }
  | { kind: "notFinalized" };

/** The release a CDT Studio Finalize just shipped, as handed to Generate Store Planograms. */
export interface ShippedRelease {
  cdtReleaseId: number;
  wallBlocking: ReleaseWallBlocking;
}

/**
 * `cdtReleaseId` is null when the studio shipped no release for the base
 * planogram, and undefined when the response did not say: a backend that
 * predates the field. Not knowing is not "not finalized", so an undefined
 * release leaves the rules on the defaults they had before the studio had a say.
 * A release whose blocking is not said is vertical, as it was before the field.
 */
export const getStudioBlockingState = ({
  isStudioAvailable,
  cdtReleaseId,
  cdtReleaseWallBlocking,
}: {
  isStudioAvailable: boolean;
  cdtReleaseId: number | null | undefined;
  cdtReleaseWallBlocking?: ReleaseWallBlocking | null;
}): StudioBlockingState => {
  if (!isStudioAvailable || cdtReleaseId === undefined) return { kind: "studioUnavailable" };
  if (cdtReleaseId === null) return { kind: "notFinalized" };
  return {
    kind: "finalized",
    releaseId: cdtReleaseId,
    wallBlocking: cdtReleaseWallBlocking ?? "vertical",
  };
};

export const defaultVerticalBlockingModeOf = (
  state: StudioBlockingState | undefined,
  fallback: boolean,
): boolean => {
  if (state?.kind === "finalized") return state.wallBlocking === "vertical";
  if (state?.kind === "notFinalized") return false;
  return fallback;
};

/** Vertical asked for with no finalized CDT to measure it against. */
export const isVerticalBlockingWithoutRelease = (
  state: StudioBlockingState | undefined,
  isVerticalBlockingMode: boolean,
): boolean => state?.kind === "notFinalized" && isVerticalBlockingMode;

/**
 * The newest release the screen knows of, per base planogram. The page seeds it
 * from the server and the studio overwrites it on Finalize, so the page does not
 * have to reload to learn what the studio just shipped.
 */
export const cdtStudioLatestReleaseAtom = omniAtom<{
  basePogId: number;
  releaseId: number | null | undefined;
  wallBlocking?: ReleaseWallBlocking | null;
} | null>({
  prefix: "cdtStudio",
  key: "latestRelease",
  value: null,
});
